import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { ActionsGridParent } from '@pages-clean/risk-assessment/constants';
import {
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import {
    GET_RISK_OBSERVATIONS_AND_ACTIONS,
    GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
    RESTORE_RISK_OBSERVATION_ACTION,
} from '@queries/risk-assessment';

export interface ActionDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    observation: ExtendedRiskObservationFields;
    action: ExtendedRiskActionFields;
    riskId: string;
    studyId: string;
    parent: ActionsGridParent;
}

export type FormInputs = {
    restoreReason: string | null;
};

const RestoreActionDialog = ({
    onClose,
    onSubmit,
    observation,
    action,
    riskId,
    studyId,
    parent,
}: ActionDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.restoreAction' });
    const { t: tCommon } = useTranslation('common');
    const [restoreRiskActionMutation] = useMutation(RESTORE_RISK_OBSERVATION_ACTION);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            restoreReason: null,
        },
    });

    const onRestore = async (data: FormInputs) => {
        const refetchQueries =
            parent === ActionsGridParent.RiskMonitoring
                ? [
                      {
                          query: GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
                          variables: {
                              studyId,
                          },
                      },
                  ]
                : [
                      {
                          query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                          variables: {
                              studyId,
                              riskId,
                          },
                      },
                  ];

        restoreRiskActionMutation({
            variables: {
                observationId: observation.id,
                actionId: action.id,
                reason: data.restoreReason,
            },
            refetchQueries,
        })
            .then(async (response) => {
                if (!response?.data?.unDeleteRiskObservationAction) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const warningText = observation.deleted ? (
        <span className={cn('k-text-left', style.fontSize)}>{t('warningMessage2')}</span>
    ) : (
        <span className={cn('k-text-left', style.fontSize)}>{t('warningMessage')}</span>
    );

    return (
        <DraggableDialog
            dataTestId="delete-action-warning"
            onSubmit={observation.deleted ? undefined : () => handleSubmit(onRestore)()}
            onClose={onClose}
            title={`${t('title')}: ${action.recordId}`}
            initialWidth={530}
            initialHeight={280}
            height={null}
            primaryBtnLabel={t('buttons.restoreAction')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnLabel={observation.deleted ? tCommon('close') : undefined}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                <InfoField type="Warning" message={warningText} />
                {!observation.deleted && (
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="restoreReason"
                        required
                        data-testid="field-action-restoreReason"
                        label={`${t('restoreReasonLabel')} *`}
                    />
                )}
            </div>
        </DraggableDialog>
    );
};

export default RestoreActionDialog;
